import { FC, useState } from 'react'

import styled from 'styled-components'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX } from '@tabler/icons-react'

import { Modal } from 'src/Components/Modal'
import { Button } from 'src/Components/Button'
import { SegmentedControl } from 'src/Components/Input/SegmentedControl'
import {
  useApproveDocumentMutation,
  useRejectDocumentMutation,
} from 'src/Redux/api/documentsApi'
import { colors } from 'src/Helpers/constants'

const Stack = styled.div<{ $gap?: string; $mt?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap || '16px'};
  margin-top: ${({ $mt }) => $mt || '0'};
`

const Group = styled.div<{ $gap?: string; $justify?: string; $mt?: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ $gap }) => $gap || '8px'};
  justify-content: ${({ $justify }) => $justify || 'flex-start'};
  margin-top: ${({ $mt }) => $mt || '0'};
`

const HintText = styled.span`
  font-size: 13px;
  color: ${colors.thirdGray};
  opacity: 0.7;
`

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  max-height: 160px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  border: 1px solid ${colors.secondGray};
  border-radius: 4px;
  color: ${colors.thirdGray};
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;

  &::placeholder {
    color: ${colors.thirdGray};
    opacity: 0.5;
  }

  &:focus {
    border-color: ${colors.fourthBlue};
  }

  &[data-error='true'] {
    border-color: ${colors.red};
  }
`

const SegmentLabel = styled.span`
  font-size: 13px;
`

interface DocumentApprovalModalProps {
  opened: boolean
  onClose: () => void
  documentId: string
  contractNumber?: string
}

type ActionType = 'approve' | 'reject'

export const DocumentApprovalModal: FC<DocumentApprovalModalProps> = ({
  opened,
  onClose,
  documentId,
  contractNumber,
}) => {
  const [action, setAction] = useState<ActionType>('approve')
  const [comment, setComment] = useState('')

  const [approveDocument, { isLoading: isApproving }] =
    useApproveDocumentMutation()
  const [rejectDocument, { isLoading: isRejecting }] =
    useRejectDocumentMutation()

  const handleSubmit = async (): Promise<void> => {
    if (action === 'approve') {
      try {
        await approveDocument({
          documentId,
          data: comment ? { comment } : undefined,
        }).unwrap()

        notifications.show({
          title: 'Успешно',
          message: 'Документ согласован',
          color: 'green',
        })
        onClose()
      } catch {
        notifications.show({
          title: 'Ошибка',
          message: 'Не удалось согласовать документ',
          color: 'red',
        })
      }
    } else {
      if (!comment.trim()) {
        notifications.show({
          title: 'Ошибка',
          message: 'Комментарий обязателен при отклонении документа',
          color: 'red',
        })
        return
      }

      try {
        await rejectDocument({
          documentId,
          data: {
            comment,
          },
        }).unwrap()

        notifications.show({
          title: 'Успешно',
          message: 'Документ отклонен и возвращен исполнителю',
          color: 'green',
        })
        onClose()
      } catch {
        notifications.show({
          title: 'Ошибка',
          message: 'Не удалось отклонить документ',
          color: 'red',
        })
      }
    }
  }

  const isLoading = isApproving || isRejecting

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Согласование документа${contractNumber ? ` №${contractNumber}` : ''}`}
      size='md'
    >
      <Stack>
        <SegmentedControl
          value={action}
          onChange={(value) => setAction(value as ActionType)}
          data={[
            {
              value: 'approve',
              label: (
                <Group $gap='6px' $justify='center'>
                  <IconCheck size={16} />
                  <SegmentLabel>Согласовать</SegmentLabel>
                </Group>
              ),
            },
            {
              value: 'reject',
              label: (
                <Group $gap='6px' $justify='center'>
                  <IconX size={16} />
                  <SegmentLabel>Отклонить</SegmentLabel>
                </Group>
              ),
            },
          ]}
          fullWidth
        />

        {action === 'approve' && (
          <>
            <HintText>
              Вы можете добавить комментарий к согласованию (необязательно)
            </HintText>
            <StyledTextarea
              placeholder='Комментарий (необязательно)'
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
            />
          </>
        )}

        {action === 'reject' && (
          <>
            <HintText>
              Укажите причину отклонения. Документ будет возвращен исполнителю.
            </HintText>
            <StyledTextarea
              placeholder='Причина отклонения *'
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
              required
              data-error={!comment.trim()}
            />
          </>
        )}

        <Group $justify='flex-end' $mt='16px'>
          <Button variant='subtle' onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            color={action === 'approve' ? 'green' : 'red'}
          >
            {action === 'approve' ? 'Согласовать' : 'Отклонить'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
